import getExclusionReason from '@app.raytd.com/common/utils/tests/getExclusionReason';
import { toast } from 'sonner';
import { Assessment } from '../assessment/assessment.slice';
import { AssetEntity } from '../asset';
import { TestCase, TestItem, TestSuite } from '../tests/entity';
import type { ReferenceImage, ReferenceImageDisposition } from '../tests/api/referenceImages';
import { TestSpecificationsEntity } from '../tests/test-specifications.slice';
import { UsersEntity } from '../users';
import { transformClassificationTree } from './elementTransformer';
import {
  ActivityItem,
  AddCustomFieldOptionRequest,
  AddCustomFieldOptionResponse,
  ClassificationItem,
  DashboardRecentResponse,
  FastFillUpdateRequest,
  GetTestItemRequest,
  PublishedReportResponse,
  PublishReportPayload,
  PublishReportResponse,
  ReportBlock,
  ReportEntity,
  ReportPayload,
  ReportResponse,
  ReportRevision,
  ReportSection,
  ReportTestItem,
  ReportUpdatePayload,
  ToggleSwitchRequest,
  ToggleSwitchResponse,
  TestItemComment,
} from './types';
import updateItemInReportCache from './updateItemInReportCache';
import {
  NotificationPagination,
  AppNotification,
  ReportExport,
} from '../notifications/types';
import { rAPI } from './rAPI';

export const assetsAPI = rAPI.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    extendAndResendInvite: builder.mutation({
      query: (invitationId) => ({
        url: `organisations/invite/${invitationId}/extend`,
        method: 'POST',
      }),
    }),

    getAsset: builder.query({
      query: (assetId) => `asset/${assetId}`,
      // providesTags: (result, error, id) => [{ type: 'Asset', id }],
      providesTags: (result, error, id) =>
        result
          ? [
              { type: 'Asset', id },
              ...(result.buildings?.map((building) => ({
                type: 'Building' as const,
                id: building.id,
              })) ?? []),
              ...(result.buildings?.flatMap((building) =>
                building.levels?.map((level) => ({
                  type: 'Level' as const,
                  id: level.id,
                }))
              ) ?? []),
              ...(result.buildings?.flatMap((building) =>
                building.levels?.flatMap((level) =>
                  level.rooms?.map((location) => ({
                    type: 'Area' as const,
                    id: location.id,
                  }))
                )
              ) ?? []),
            ]
          : [{ type: 'Asset', id }],
    }),

    createAsset: builder.mutation({
      query: ({ organisationId, ...asset }) => ({
        url: `organisations/${organisationId}/assets`,
        method: 'POST',
        body: asset,
      }),
      invalidatesTags: [{ type: 'Assets', id: 'LIST' }],
    }),

    updateAsset: builder.mutation({
      query: ({ id, ...asset }) => ({
        url: `/asset/${id}`,
        method: 'PUT',
        body: asset,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Assets', id: 'LIST' },
      ],
    }),

    retireAsset: builder.mutation({
      query: (assetId) => ({
        url: `/asset/${assetId}/retire`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Asset', id },
        { type: 'Assets', id: 'LIST' },
      ],
    }),

    changeUserRole: builder.mutation({
      query: ({ assetId, user_id, role }) => ({
        url: `/asset/${assetId}/role`,
        method: 'PUT',
        body: {
          user_id,
          role,
        },
      }),
      invalidatesTags: ['Asset'],
    }),

    archiveAsset: builder.mutation({
      query: (assetId) => ({
        url: `asset/${assetId}/archive`,
        method: 'PATCH',
      }),
      async onQueryStarted(assetId, lifecycleApi) {
        const getAssetsPatchResult = lifecycleApi.dispatch(
          rAPI.util.updateQueryData('getAssets', undefined, (draft) => {
            const asset = draft.entities[assetId];

            if (asset) {
              if (asset.status === 'archived') {
                asset.status = 'active';
                asset.is_globally_archived = false;
              } else {
                asset.status = 'archived';
                asset.is_globally_archived = true;
              }
            }
          })
        );

        try {
          await lifecycleApi.queryFulfilled;
          //          const state = lifecycleApi.getState();
          //        const updatedAsset = state.assetApi.queries['getAssets']?.data?.entities[assetId];

          //      toast.success(`Asset ${updatedAsset.status === 'active' ? 'restored' : 'archived'} successfully`);
          toast.success(`Asset updated`);
        } catch (error) {
          console.error('Error archiving asset', error);
          toast.error('Error archiving asset');
          getAssetsPatchResult.undo();
        }
      },
      // invalidatesTags: ['Asset'],
    }),

    archiveAssetForUser: builder.mutation({
      query: (assetId) => ({
        url: `asset/${assetId}/status`,
        method: 'PATCH',
      }),
      async onQueryStarted(assetId, lifecycleApi) {
        const getAssetsPatchResult = lifecycleApi.dispatch(
          rAPI.util.updateQueryData('getAssets', undefined, (draft) => {
            const asset = draft.entities[assetId];

            if (asset) {
              if (asset.status === 'archived') {
                asset.status = 'active';
              } else {
                asset.status = 'archived';
              }
            }
          })
        );

        try {
          await lifecycleApi.queryFulfilled;
          //          const state = lifecycleApi.getState();
          //        const updatedAsset = state.assetApi.queries['getAssets']?.data?.entities[assetId];

          //      toast.success(`Asset ${updatedAsset.status === 'active' ? 'restored' : 'archived'} successfully`);
          toast.success(`Asset updated`);
        } catch (error) {
          console.error('Error archiving asset', error);
          toast.error('Error archiving asset');
          getAssetsPatchResult.undo();
        }
      },
      // invalidatesTags: ['Asset'],
    }),

    getAssetLocations: builder.query({
      query: (assetId) => `asset/${assetId}/locations`,
    }),

    addCollaborator: builder.mutation({
      query: ({ assetId, ...collaborator }) => ({
        url: `asset/${assetId}/collaborators`,
        method: 'POST',
        body: collaborator,
      }),
      invalidatesTags: ['Asset'],
    }),

    removeCollaborator: builder.mutation({
      query: ({ assetId, userId }) => ({
        url: `asset/${assetId}/collaborators`,
        method: 'DELETE',
        body: { user_id: userId },
      }),
      invalidatesTags: ['Asset'],
    }),

    uploadCoverImage: builder.mutation({
      query: ({ assetId, coverImage }) => {
        const formData = new FormData();
        formData.append('cover_image', coverImage);
        return {
          url: `asset/${assetId}/cover`,
          method: 'POST',
          body: formData,
          headers: {
            // Explicitly remove Content-Type header if it's being set somewhere
            'Content-Type': undefined,
          },
        };
      },
      invalidatesTags: ['CoverImage'],
    }),

    deleteCoverImage: builder.mutation({
      query: (assetId) => ({
        url: `asset/${assetId}/cover`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CoverImage'],
    }),

    getAssetBuildings: builder.query({
      query: (assetId) => `asset/${assetId}/buildings`,
      providesTags: (result) =>
        result
          ? [
              ...result.map((building) => ({
                type: 'Building' as const,
                id: building.id,
              })),
              { type: 'Building', id: 'LIST' },
            ]
          : [{ type: 'Building', id: 'LIST' }],
    }),

    addBuilding: builder.mutation({
      query: (building) => ({
        url: `buildings`,
        method: 'POST',
        body: building,
      }),
      invalidatesTags: (result, error, { asset_id }) => [
        { type: 'Building', id: 'LIST' },
        { type: 'Asset', id: asset_id },
      ],
      async onQueryStarted(building, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          assetsAPI.util.updateQueryData(
            'getAssetBuildings',
            building.asset_id,
            (draft) => {
              draft.push({
                ...building,
                status: 'pending',
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // If the query fails, `patchResult.undo()` will roll back the optimistic update
          patchResult.undo();
        }
      },
    }),

    updateBuilding: builder.mutation({
      query: ({ building_id, ...building }) => ({
        url: `buildings/${building_id}`,
        method: 'PUT',
        body: building,
      }),
      invalidatesTags: (result, error, { id, asset_id }) => [
        { type: 'Building', id },
        { type: 'Asset', id: asset_id },
      ],
      async onQueryStarted(
        { building_id, asset_id, ...patch },
        { dispatch, queryFulfilled }
      ) {
        // `patch` contains the data we're sending to the server
        const patchResult = dispatch(
          // updateQueryData takes the endpoint name and query args to find the correct cache entry
          assetsAPI.util.updateQueryData('getAssetBuildings', asset_id, (draft) => {
            // `draft` is an Immer-powered proxy of the current cache data
            const building = draft.find((b) => b.id === building_id);
            if (building) {
              // Mutate the draft object directly
              Object.assign(building, patch);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          // If the query fails, `patchResult.undo()` will roll back the optimistic update
          patchResult.undo();
        }
      },
    }),

    getAssetLevels: builder.query({
      query: (assetId) => `asset/${assetId}/levels`,
      providesTags: (result) =>
        result
          ? [
              ...result.map((level) => ({
                type: 'Level' as const,
                id: level.id,
              })),
              { type: 'Level', id: 'LIST' },
            ]
          : [{ type: 'Level', id: 'LIST' }],
    }),

    addAssetLevel: builder.mutation({
      query: (level) => ({
        url: `levels`,
        method: 'POST',
        body: level,
      }),
      invalidatesTags: (result, error, { asset_id, building_id }) => [
        { type: 'Level', id: 'LIST' },
        { type: 'Building', id: building_id },
        { type: 'Asset', id: asset_id },
      ],
      async onQueryStarted(level, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          assetsAPI.util.updateQueryData(
            'getAssetLevels',
            level.asset_id,
            (draft) => {
              draft.push({
                ...level,
                status: 'pending',
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // If the query fails, `patchResult.undo()` will roll back the optimistic update
          patchResult.undo();
        }
      },
    }),

    updateLevel: builder.mutation({
      query: ({ level_id, ...level }) => ({
        url: `levels/${level_id}`,
        method: 'PUT',
        body: level,
      }),
      invalidatesTags: (result, error, { id, asset_id, building_id }) => [
        { type: 'Level', id },
        { type: 'Building', id: building_id },
        { type: 'Asset', id: asset_id },
      ],
    }),

    getAssetAreas: builder.query({
      query: (assetId) => `asset/${assetId}/areas`,
      providesTags: (result) =>
        result
          ? [
              ...result.map((area) => ({ type: 'Area' as const, id: area.id })),
              { type: 'Area', id: 'LIST' },
            ]
          : [{ type: 'Area', id: 'LIST' }],
    }),

    addAssetArea: builder.mutation({
      query: (area) => ({
        url: `rooms`,
        method: 'POST',
        body: area,
      }),
      invalidatesTags: (result, error, { asset_id, building_id, level_id }) => [
        { type: 'Area', id: 'LIST' },
        { type: 'Level', id: level_id },
        { type: 'Building', id: building_id },
        { type: 'Asset', id: asset_id },
      ],
      async onQueryStarted(area, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          assetsAPI.util.updateQueryData('getAssetAreas', area.asset_id, (draft) => {
            draft.push({
              ...area,
              status: 'pending',
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          // If the query fails, `patchResult.undo()` will roll back the optimistic update
          patchResult.undo();
        }
      },
    }),

    updateArea: builder.mutation({
      query: ({ room_id, ...area }) => ({
        url: `rooms/${room_id}`,
        method: 'PUT',
        body: area,
      }),
      invalidatesTags: (
        result,
        error,
        { room_id, asset_id, building_id, level_id }
      ) => [
        { type: 'Area', id: room_id },
        { type: 'Level', id: level_id },
        { type: 'Building', id: building_id },
        { type: 'Asset', id: asset_id },
      ],
    }),
  }),
});

export const {
  useExtendAndResendInviteMutation,
  useGetAssetQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useRetireAssetMutation,
  useArchiveAssetMutation,
  useArchiveAssetForUserMutation,
  useGetAssetLocationsQuery,
  useAddCollaboratorMutation,
  useRemoveCollaboratorMutation,
  useUploadCoverImageMutation,
  useDeleteCoverImageMutation,
  useGetAssetBuildingsQuery,
  useAddBuildingMutation,
  useUpdateBuildingMutation,
  useGetAssetLevelsQuery,
  useUpdateLevelMutation,
  useAddAssetLevelMutation,
  useGetAssetAreasQuery,
  useAddAssetAreaMutation,
  useUpdateAreaMutation,
  useChangeUserRoleMutation,
} = assetsAPI;
